import { cva, VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import React from 'react';

import {
  POSITION_SCALE,
  SURFACE_PADDING,
  SIZE_SCALE,
  TYPE_SCALE,
  Z_INDEX,
  GAP_SCALE,
  PADDING_X,
  PADDING_RIGHT,
  MARGIN_TOP,
  RADIUS_SCALE,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

const ToastProvider = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ ...props }: React.HTMLAttributes<HTMLDivElement>, ref: React.Ref<HTMLDivElement>) => (
    <div
      ref={ref}
      className={`fixed ${POSITION_SCALE.top['0']} z-${Z_INDEX.overlay} flex max-h-screen w-full flex-col-reverse ${SURFACE_PADDING['4']} sm:${POSITION_SCALE.bottom['0']} sm:${POSITION_SCALE.right['0']} sm:top-auto sm:flex-col md:${SIZE_SCALE.w64}`}
      {...props}
    />
  )
);
ToastProvider.displayName = 'ToastProvider';

const ToastViewport = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ ...props }: React.HTMLAttributes<HTMLDivElement>, ref: React.Ref<HTMLDivElement>) => (
    <div
      ref={ref}
      className={`fixed ${POSITION_SCALE.top['0']} z-${Z_INDEX.overlay} flex max-h-screen w-full flex-col-reverse ${SURFACE_PADDING['4']} sm:${POSITION_SCALE.bottom['0']} sm:${POSITION_SCALE.right['0']} sm:top-auto sm:flex-col md:${SIZE_SCALE.w64}`}
      {...props}
    />
  )
);
ToastViewport.displayName = 'ToastViewport';

const toastVariants = cva(
  `group pointer-events-auto relative flex ${SIZE_SCALE.full} items-center justify-between ${GAP_SCALE.md} overflow-hidden ${RADIUS_SCALE.xl} border ${SURFACE_PADDING['4']} ${PADDING_RIGHT['8']} shadow-xl transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full backdrop-blur-sm`,
  {
    variants: {
      variant: {
        default: 'border-success bg-success/10 text-foreground shadow-success/20',
        destructive:
          'destructive group border-destructive bg-destructive/10 text-foreground shadow-destructive/20',
        welcome: 'border-border bg-background text-foreground shadow-foreground/10 border-2',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface ToastProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof toastVariants> {
  className?: string;
  variant?: 'default' | 'destructive' | null;
}

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ className, variant, ...props }: ToastProps, ref: React.Ref<HTMLDivElement>) => {
    return <div ref={ref} className={cn(toastVariants({ variant }), className)} {...props} />;
  }
);
Toast.displayName = 'Toast';

const ToastAction = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (
    { className, ...props }: React.HTMLAttributes<HTMLDivElement>,
    ref: React.Ref<HTMLDivElement>
  ) => (
    <div
      ref={ref}
      className={cn(
        `inline-flex ${SIZE_SCALE['8']} shrink-0 items-center justify-center ${RADIUS_SCALE.md} border bg-transparent ${PADDING_X['3']} ${TYPE_SCALE.sm} font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive`,
        className
      )}
      {...props}
    />
  )
);
ToastAction.displayName = 'ToastAction';

const ToastClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(
  (
    { className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>,
    ref: React.Ref<HTMLButtonElement>
  ) => (
    <button
      ref={ref}
      className={cn(
        `absolute ${POSITION_SCALE.right['2']} ${POSITION_SCALE.top['2']} ${RADIUS_SCALE.lg} ${SURFACE_PADDING['1.5']} text-muted-foreground opacity-100 transition-all hover:text-foreground hover:bg-background/80 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring group-[.destructive]:text-destructive group-[.destructive]:hover:text-destructive group-[.destructive]:hover:bg-background/80 group-[.destructive]:focus:ring-destructive`,
        className
      )}
      toast-close=""
      {...props}
    >
      <X className={`${SIZE_SCALE['4']} ${SIZE_SCALE.w4}`} />
    </button>
  )
);
ToastClose.displayName = 'ToastClose';

const ToastTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (
    { className, ...props }: React.HTMLAttributes<HTMLDivElement>,
    ref: React.Ref<HTMLDivElement>
  ) => (
    <div
      ref={ref}
      className={cn(`${TYPE_SCALE.base} font-bold text-foreground`, className)}
      {...props}
    />
  )
);
ToastTitle.displayName = 'ToastTitle';

const ToastDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (
    { className, ...props }: React.HTMLAttributes<HTMLDivElement>,
    ref: React.Ref<HTMLDivElement>
  ) => (
    <div
      ref={ref}
      className={cn(`${TYPE_SCALE.sm} text-muted-foreground ${MARGIN_TOP['1']}`, className)}
      {...props}
    />
  )
);
ToastDescription.displayName = 'ToastDescription';

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
